import { Box, type SxProps, type Theme } from "@mui/material";
import { GRID_FONT, GRID_RADIUS, INK, NEUTRAL } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";

export type TagChipProps = {
	/** The short, already-translated label to display (e.g. a department name, a provenance tag). */
	label: string;
	/**
	 * Full, already-translated hover text — the label is ellipsized to its container. Omit only for a
	 * label that can never truncate (a fixed-width time range, a two-letter code): a tooltip that just
	 * repeats what is already fully visible is noise the user has to dismiss.
	 */
	tooltip?: string;
	/**
	 * Tabular variant: renders the label in the monospace face so a column of chips aligns
	 * character-for-character — for values read digit-by-digit (a time range, an id, a short code).
	 * Also steps the size up one notch, because a mono face reads optically smaller at the same px and
	 * a numeric value has to stay legible where a word tag does not.
	 */
	mono?: boolean;
	/** Layout overrides — the caller owns width/alignment (`ml: auto` vs a fixed width), not the chip's ink. */
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * A subtle, read-only neutral micro-tag for dense rows: a small labelled pill that annotates
 * an item (provenance, category, a short status, a time range) without competing with primary
 * content — ellipsizes to its container with the full label in a tooltip. Generic over any "small
 * read-only label"; the caller drives only width/alignment via `sx`. For interactive/selectable
 * chips use `SystemChips`; for domain status use `EmployeeStatusChip`.
 */
export const TagChip = ({ label, tooltip, mono, sx, dataTestId }: TagChipProps) => {
	const chip = (
		<Box
			component="span"
			data-testid={dataTestId}
			sx={[
				{
					px: 0.625,
					py: "1px",
					borderRadius: GRID_RADIUS.CHIP,
					backgroundColor: NEUTRAL[150],
					color: INK.MUTED,
					fontSize: mono ? GRID_FONT.SM : GRID_FONT.MICRO,
					fontWeight: 600,
					lineHeight: 1.5,
					letterSpacing: GRID_FONT.LETTER_SPACING.TIGHT,
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
					// The generic CSS family, matching every other mono surface in the app (PBJ id columns,
					// the feature-flag values, ReadOnlyMetadataField) — deliberately not a bespoke stack.
					...(mono && { fontFamily: "monospace" }),
				},
				...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{label}
		</Box>
	);
	return tooltip === undefined ? chip : <InHouseTooltip title={tooltip}>{chip}</InHouseTooltip>;
};
